import { jsPDF } from 'jspdf';
import JsBarcode from 'jsbarcode';

const num = (v, fallback) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

const escapeTsplText = (s) => String(s || '').replace(/"/g, "'");

/** Build TSPL for TSC TDP-225 (and compatible). */
export const buildTspl = (label, printer) => {
  const widthMm = num(printer?.labelWidthMm, 50);
  const heightMm = num(printer?.labelHeightMm, 30);
  const gapMm = num(printer?.gapMm, 2);
  const copies = Math.max(1, Math.floor(Number(label.copies) || 1));
  const dpi = num(printer?.dpi, 203);
  const widthDots = Math.round((widthMm / 25.4) * dpi);

  const lines = [
    `SIZE ${widthMm} mm, ${heightMm} mm`,
    `GAP ${gapMm} mm, 0 mm`,
    'CLS',
    `TEXT 16,12,"2",0,1,1,"${escapeTsplText(label.code)}"`,
  ];
  // Qty top-right (approximate right align via fixed x near right edge)
  if (label.qtyText) {
    const qtyX = Math.max(16, widthDots - 120);
    lines.push(`TEXT ${qtyX},12,"2",0,1,1,"${escapeTsplText(label.qtyText)}"`);
  }
  lines.push(`TEXT 16,40,"2",0,1,1,"${escapeTsplText(label.name).slice(0, 28)}"`);
  // Taller barcode — starts earlier without qty row in the middle
  const barcodeY = 72;
  const barcodeH = Math.max(80, Math.round((heightMm / 25.4) * dpi) - barcodeY - 16);
  if (label.barcode) {
    lines.push(`BARCODE 16,${barcodeY},"128",${barcodeH},1,0,2,2,"${escapeTsplText(label.barcode)}"`);
  }
  lines.push(`PRINT ${copies},1`);
  return `${lines.join('\r\n')}\r\n`;
};

const wrapCanvasText = (ctx, text, maxWidth) => {
  const words = String(text || '').split(/\s+/).filter(Boolean);
  if (!words.length) return [];
  const lines = [];
  let current = words[0];
  for (let i = 1; i < words.length; i += 1) {
    const trial = `${current} ${words[i]}`;
    if (ctx.measureText(trial).width <= maxWidth) {
      current = trial;
    } else {
      lines.push(current);
      current = words[i];
    }
  }
  lines.push(current);
  return lines;
};

const barcodeCanvas = (barcodeValue, targetWidthPx, targetHeightPx) => {
  const canvas = document.createElement('canvas');
  const value = String(barcodeValue || '0');
  // Approximate module width so Code128 roughly fills targetWidthPx
  const estimatedModules = Math.max(40, value.length * 11 + 20);
  const moduleWidth = Math.max(1, Math.floor(targetWidthPx / estimatedModules));
  JsBarcode(canvas, value, {
    format: 'CODE128',
    displayValue: true,
    fontSize: Math.max(12, Math.round(targetWidthPx * 0.04)),
    height: Math.max(28, Math.round(targetHeightPx * 0.7)),
    margin: 0,
    width: moduleWidth,
  });
  return canvas;
};

/**
 * Render label to canvas with browser fonts (Cyrillic-safe), then embed in PDF.
 * Helvetica in jsPDF has no Cyrillic glyphs — do not draw text via doc.text.
 */
const renderLabelCanvas = (label, widthMm, heightMm) => {
  const scale = 8; // ~203 dpi-ish for preview sharpness
  const w = Math.max(1, Math.round(widthMm * scale));
  const h = Math.max(1, Math.round(heightMm * scale));
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) return canvas;

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, w, h);
  ctx.fillStyle = '#000000';
  ctx.textBaseline = 'top';

  const pad = Math.round(w * 0.04);
  const maxTextW = w - pad * 2;
  let y = pad;

  const codeSize = Math.max(14, Math.round(h * 0.12));
  const qtySize = Math.max(12, Math.round(h * 0.12));
  const qtyText = label.qtyText ? String(label.qtyText) : '';

  // Qty top-right — frees vertical space for a taller barcode
  let qtyBlockW = 0;
  if (qtyText) {
    ctx.font = `bold ${qtySize}px Arial, "DejaVu Sans", "Noto Sans", sans-serif`;
    qtyBlockW = ctx.measureText(qtyText).width + Math.round(w * 0.03);
    ctx.fillText(qtyText, w - pad - ctx.measureText(qtyText).width, y);
  }

  ctx.font = `bold ${codeSize}px Arial, "DejaVu Sans", "Noto Sans", sans-serif`;
  const codeMaxW = Math.max(40, maxTextW - qtyBlockW);
  const code = String(label.code || '');
  // Keep code from overlapping qty
  let codeDraw = code;
  while (codeDraw.length > 1 && ctx.measureText(codeDraw).width > codeMaxW) {
    codeDraw = codeDraw.slice(0, -1);
  }
  ctx.fillText(codeDraw, pad, y);
  y += Math.max(codeSize, qtyText ? qtySize : 0) + Math.round(h * 0.04);

  const nameSize = Math.max(11, Math.round(h * 0.1));
  ctx.font = `${nameSize}px Arial, "DejaVu Sans", "Noto Sans", sans-serif`;
  const nameLines = wrapCanvasText(ctx, label.name, maxTextW).slice(0, 2);
  const nameLineH = Math.round(nameSize * 1.15);
  nameLines.forEach((line) => {
    ctx.fillText(line, pad, y);
    y += nameLineH;
  });
  y += Math.round(h * 0.02);

  if (label.barcode) {
    const availH = Math.max(24, h - y - pad);
    const barW = maxTextW;
    const barH = availH;
    const barSrc = barcodeCanvas(label.barcode, barW, barH);
    ctx.drawImage(barSrc, pad, y, barW, barH);
  }

  return canvas;
};

/** Generate a multi-page PDF (one page per copy) and open it. */
export const openLabelsPdf = (label, printer) => {
  const widthMm = Math.min(54, num(printer?.labelWidthMm, 50));
  const heightMm = num(printer?.labelHeightMm, 30);
  const copies = Math.max(1, Math.floor(Number(label.copies) || 1));

  const doc = new jsPDF({
    orientation: widthMm >= heightMm ? 'l' : 'p',
    unit: 'mm',
    format: [widthMm, heightMm],
  });

  const labelCanvas = renderLabelCanvas(label, widthMm, heightMm);
  const imgData = labelCanvas.toDataURL('image/png');

  for (let i = 0; i < copies; i += 1) {
    if (i > 0) doc.addPage([widthMm, heightMm]);
    doc.addImage(imgData, 'PNG', 0, 0, widthMm, heightMm);
  }

  const blobUrl = doc.output('bloburl');
  window.open(blobUrl, '_blank');
};

export const postTsplToPrinter = async (tspl, printer) => {
  const host = String(printer?.host || '').trim();
  const port = num(printer?.port, 9101);
  if (!host) {
    throw new Error('Не вказано host принтера');
  }
  const url = `http://${host}:${port}/print`;
  const headers = {
    'Content-Type': 'text/plain; charset=utf-8',
  };
  const token = String(printer?.authToken || '').trim();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  const res = await fetch(url, {
    method: 'POST',
    headers,
    body: tspl,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(text || `Помилка друку (${res.status})`);
  }
};

/**
 * DirectPrint → TSPL HTTP; otherwise open PDF.
 */
export const printLabels = async (label, printer) => {
  if (printer && printer.directPrint) {
    const tspl = buildTspl(label, printer);
    await postTsplToPrinter(tspl, printer);
    return { mode: 'direct' };
  }
  openLabelsPdf(label, printer || {});
  return { mode: 'pdf' };
};
